"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import type { CBlogArticle as CBlogArticleData } from "@/lib/api/cms-catalog";
import type { CBlogTranslations } from "./CBlog.i18n";
import { ParagraphRenderer } from "@/components/cms/Paragraphs/ParagraphRenderer";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CBlog.module.css";

/* Biblioteka CMS Interakcje (komentarze + reakcje) - ladowana leniwie
   (ssr:false), montuje sie i dociaga dane dopiero po wyrenderowaniu wpisu. */
const ArticleInteractions = dynamic(
  () =>
    import("@/components/cms/Interactions/ArticleInteractions").then(
      (m) => m.ArticleInteractions,
    ),
  { ssr: false },
);

/**
 * Widok pojedynczego wpisu bloga (c_blog?view=article).
 *
 * Zdjecie glowne renderujemy przez `<picture>` - browser sam wybiera wariant
 * per viewport bez JS. Warianty tablet/phone (`image_tablet`/`image_phone`
 * ustawione w panelu admina) sa opcjonalne - gdy null, browser uzywa desktop
 * fallback z `<img src>`.
 */
export function CBlogArticle({
  article,
  listHref,
  t,
}: {
  article: CBlogArticleData;
  listHref: string | null;
  t: CBlogTranslations;
}) {
  const backHref = listHref ? extractInternalHref(listHref) : null;

  return (
    <article className={styles.article}>
      {(backHref !== null || listHref) && (
        <div className={styles.articleBack}>
          {backHref !== null ? (
            <Link href={backHref} className={styles.articleBackLink}>
              {t.backToList}
            </Link>
          ) : (
            <a href={listHref!} className={styles.articleBackLink}>
              {t.backToList}
            </a>
          )}
        </div>
      )}

      <header className={styles.articleHeader}>
        <h2 className={styles.articleTitle}>{article.name}</h2>
        <div className={styles.articleMeta}>
          {article.publication_date && (
            <time className={styles.articleDate} dateTime={article.publication_date}>
              {t.formatDate(article.publication_date)}
            </time>
          )}
          {article.author?.name && (
            <span className={styles.articleAuthor}>{t.byAuthor(article.author.name)}</span>
          )}
        </div>
        {article.categorys && article.categorys.length > 0 && (
          <ul className={styles.articleCategories}>
            {article.categorys.map((cat, i) => {
              const catHref = extractInternalHref(cat.href);
              return (
                <li key={`${cat.id_catalog}-${i}`} className={styles.articleCategory}>
                  {catHref !== null ? (
                    <Link href={catHref}>{cat.name}</Link>
                  ) : (
                    <a href={cat.href}>{cat.name}</a>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </header>

      {article.picture && (
        <figure className={styles.articleMainImage}>
          <picture>
            {article.picture.full_src_phone && (
              <source srcSet={article.picture.full_src_phone} media="(max-width: 480px)" />
            )}
            {article.picture.full_src_tablet && (
              <source srcSet={article.picture.full_src_tablet} media="(max-width: 1024px)" />
            )}
            {/* Bez width/height na <img> - warianty tablet/phone maja inny
                aspect-ratio niz desktop (1024x720, 450x500 vs 1920x740), wiec
                jednolite atrybuty rezerwowalyby zla wysokosc na mniejszych
                breakpointach (CLS + wizualne rozjechanie do wczytania).
                Browser policzy aspect-ratio z faktycznie zaladowanego wariantu. */}
            <img
              src={article.picture.full_src}
              alt={article.picture.alt || article.name}
              loading="eager"
            />
          </picture>
        </figure>
      )}

      {article.short_text && (
        <div
          className={styles.articleLead}
          dangerouslySetInnerHTML={{ __html: article.short_text }}
        />
      )}

      {article.paragraphs && article.paragraphs.length > 0 && (
        <ParagraphRenderer paragraphs={article.paragraphs} />
      )}

      {/* Reakcje + komentarze (biblioteka CMS Interakcje) - lazy, pod tresca
          wpisu. Reakcje renderowane nad komentarzami. */}
      {article.interactions &&
        (article.interactions.reactions?.enabled ||
          article.interactions.comments?.enabled) && (
          <ArticleInteractions interactions={article.interactions} />
        )}
    </article>
  );
}
